import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  FileText, Search, Share2, Clock, Coffee, Shirt, 
  ShieldAlert, CheckSquare, Calendar, Briefcase, BookOpen, 
  HeartHandshake, ShieldCheck, Users, ArrowUp 
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent } from '../../components/ui/card';
import { ScrollArea } from '../../components/ui/scroll-area';
import { policies } from '../../data/policies';
import { toast } from 'sonner';
import logo from '../../assets/teamly.png';

// Map each policy to a relevant icon
const iconMap = {
  'attendance-policy': Clock,
  'cafeteria-guidelines': Coffee,
  'dress-code-policy': Shirt,
  'health-safety-guidelines': ShieldAlert,
  'general-do-s-don-ts': CheckSquare,
  'leave-policy': Calendar,
  'work-rules': Briefcase,
  'office-guidelines': BookOpen,
  'posh-policy': HeartHandshake,
  'security-guidelines': ShieldCheck,
  'shared-space-etiquettes': Users,
};

const CompanyPolicies = () => {
  const [activeTab, setActiveTab] = useState(policies[0]?.id || '');
  const [isManualScroll, setIsManualScroll] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);
  
  const sectionRefs = useRef({});
  const tabsListRef = useRef(null);
  const observerRef = useRef(null);
  const manualScrollTimeout = useRef(null);

  // Check if page is accessed on the standalone public path
  const isPublicPath = useMemo(() => {
    return window.location.pathname === '/company-policies';
  }, []);

  // Helper: Retrieve Lucide icon for a given policy ID
  const getPolicyIcon = useCallback((id) => {
    const IconComponent = iconMap[id] || FileText;
    return <IconComponent className="h-4 w-4" />;
  }, []);

  // Helper: Calculate reading time of HTML content
  const getReadingTime = useCallback((htmlContent) => {
    const text = htmlContent.replace(/<[^>]*>/g, ''); // strip HTML tags
    const words = text.trim().split(/\s+/).length;
    const wpm = 200; // average reading words per minute
    const time = Math.ceil(words / wpm);
    return `${time} min read`;
  }, []);

  // Filter policies based on title or content query
  const filteredPolicies = useMemo(() => {
    return policies.filter((policy) => {
      const titleMatch = policy.title.toLowerCase().includes(searchQuery.toLowerCase());
      const textContent = policy.contentHtml.replace(/<[^>]*>/g, '').toLowerCase();
      const contentMatch = textContent.includes(searchQuery.toLowerCase());
      return titleMatch || contentMatch;
    });
  }, [searchQuery]);

  // Set up IntersectionObserver for scrollspy
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '-15% 0px -60% 0px',
      threshold: 0,
    };

    observerRef.current = new IntersectionObserver((entries) => {
      if (isManualScroll) return;

      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

      if (visible.length > 0) {
        const id = visible[0].target.id;
        setActiveTab(id);
      }
    }, options);

    filteredPolicies.forEach((policy) => {
      const el = sectionRefs.current[policy.id];
      if (el) observerRef.current.observe(el);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [isManualScroll, filteredPolicies]);

  // Scroll tab trigger into view when active tab changes
  useEffect(() => {
    if (tabsListRef.current) {
      const activeTrigger = tabsListRef.current.querySelector(`[data-value="${activeTab}"]`);
      if (activeTrigger) {
        activeTrigger.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }
  }, [activeTab]);

  // Track window scroll for the Back to Top button
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle scroll to a section on tab click
  const handleTabClick = useCallback((id) => {
    setActiveTab(id);
    setIsManualScroll(true);

    const el = sectionRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    if (manualScrollTimeout.current) {
      clearTimeout(manualScrollTimeout.current);
    }
    manualScrollTimeout.current = setTimeout(() => {
      setIsManualScroll(false);
    }, 1000);
  }, []);

  // Handle initial hash route if hash present in URL
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const id = hash.replace('#', '');
      const el = sectionRefs.current[id];
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setActiveTab(id);
        }, 500);
      }
    }
  }, []);

  // Handle copy section link to clipboard
  const handleCopyLink = useCallback((id) => {
    const url = `${window.location.origin}/company-policies#${id}`;
    navigator.clipboard.writeText(url)
      .then(() => {
        toast.success('Policy link copied to clipboard!');
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
        toast.error('Failed to copy link.');
      });
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Safe title highlight
  const highlightText = (text, highlight) => {
    if (!highlight.trim()) return text;
    const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
    return (
      <span>
        {parts.map((part, i) =>
          part.toLowerCase() === highlight.toLowerCase() ? (
            <mark key={i} className="bg-amber-100 text-amber-900 rounded px-0.5 font-semibold">
              {part}
            </mark>
          ) : (
            part
          )
        )}
      </span>
    );
  };

  // Main page content renderer (used for both public/standalone and private layout routes)
  const renderPageContent = () => {
    const stickyTopClass = isPublicPath ? 'top-16' : 'top-14';

    return (
      <div className="space-y-6 animate-slide-up">
        {/* Page Header & Search Bar */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 pb-2">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-2xl text-primary shadow-inner">
              <FileText className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3.5xl font-extrabold tracking-tight text-slate-900 leading-none">
                Company Policies
              </h1>
              <p className="text-slate-500 text-sm sm:text-base mt-1.5 font-medium">
                Guidelines, regulations, and workplace policies for all employees
              </p>
            </div>
          </div>

          {/* Search Input */}
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search policies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-16 py-2.5 text-sm bg-white border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400 hover:text-slate-600 bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {filteredPolicies.length === 0 ? (
          /* Empty State */
          <div className="text-center py-20 bg-white border border-slate-100 rounded-2xl shadow-sm animate-fade-in">
            <div className="p-4 bg-slate-50 text-slate-400 rounded-full inline-flex mb-4">
              <Search className="h-10 w-10" />
            </div>
            <h3 className="text-lg font-bold text-slate-700">No Policies Found</h3>
            <p className="text-slate-500 mt-1 max-w-sm mx-auto text-sm">
              We couldn't find any policies matching "{searchQuery}". Try searching for "leave", "hours", or "safety".
            </p>
            <button
              onClick={() => setSearchQuery('')}
              className="mt-6 px-4 py-2 text-sm font-semibold text-primary bg-primary/5 hover:bg-primary/10 rounded-xl transition-all"
            >
              Clear Search
            </button>
          </div>
        ) : (
          <>
            {/* Sticky Tabs Bar (Mobile & Tablet viewports) */}
            <div className={`lg:hidden sticky ${stickyTopClass} z-30 bg-slate-50/95 backdrop-blur-sm py-3 border-b border-slate-100 -mx-4 px-4 sm:-mx-6 sm:px-6`}>
              <Tabs value={activeTab} onValueChange={handleTabClick} className="w-full">
                <ScrollArea className="w-full whitespace-nowrap">
                  <TabsList
                    ref={tabsListRef}
                    className="bg-transparent gap-2 h-auto p-0 mb-0 inline-flex w-max"
                  >
                    {filteredPolicies.map((policy) => {
                      const isActive = activeTab === policy.id;
                      return (
                        <TabsTrigger
                          key={policy.id}
                          value={policy.id}
                          className={`rounded-full px-4 py-2 h-auto text-xs font-semibold border transition-all whitespace-nowrap flex items-center gap-2 ${
                            isActive
                              ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                              : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          {getPolicyIcon(policy.id)}
                          {policy.title}
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>
                </ScrollArea>
              </Tabs>
            </div>

            {/* Grid Layout containing Navigation Sidebar & Content Feed */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              
              {/* Desktop Navigation Sidebar (3 columns) */}
              <div className="hidden lg:block lg:col-span-4 sticky top-6">
                <div className="p-4 bg-white rounded-2xl border border-slate-100 shadow-[0_4px_25px_rgba(0,0,0,0.015)] max-h-[calc(100vh-100px)] overflow-y-auto pr-1">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 px-3">
                    Policies Index
                  </h3>
                  <div className="space-y-1">
                    {filteredPolicies.map((policy) => {
                      const isActive = activeTab === policy.id;
                      return (
                        <button
                          key={policy.id}
                          onClick={() => handleTabClick(policy.id)}
                          className={`w-full flex items-start gap-3.5 p-3 rounded-xl text-left transition-all duration-200 group ${
                            isActive
                              ? 'bg-primary/5 text-primary border-l-4 border-primary pl-2.5 font-bold'
                              : 'text-slate-600 hover:bg-slate-50 border-l-4 border-transparent pl-2.5 hover:text-slate-900'
                          }`}
                        >
                          <div className={`p-2 rounded-lg transition-colors ${
                            isActive ? 'bg-primary/10 text-primary' : 'bg-slate-50 text-slate-400 group-hover:bg-white group-hover:text-slate-500'
                          }`}>
                            {getPolicyIcon(policy.id)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold truncate leading-snug">{policy.title}</div>
                            <div className="text-[11px] text-slate-400 font-medium mt-0.5">{getReadingTime(policy.contentHtml)}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Scrollable Policy Contents (8 columns) */}
              <div className="lg:col-span-8 space-y-8 pb-20">
                {filteredPolicies.map((policy) => {
                  const isCurrentActive = activeTab === policy.id;
                  return (
                    <section
                      key={policy.id}
                      id={policy.id}
                      ref={(el) => {
                        sectionRefs.current[policy.id] = el;
                      }}
                      className="scroll-mt-28 lg:scroll-mt-6"
                    >
                      <Card className={`bg-white border rounded-2xl shadow-[0_4px_30px_rgba(0,0,0,0.02)] transition-all duration-300 hover:shadow-[0_10px_40px_rgba(0,0,0,0.04)] overflow-hidden ${
                        isCurrentActive ? 'border-primary/20 ring-1 ring-primary/5' : 'border-slate-100'
                      }`}>
                        <CardContent className="p-6 sm:p-10">
                          {/* Policy Card Header */}
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-6 mb-6 border-b border-slate-100">
                            <div className="flex items-center gap-4">
                              <div className={`p-3 rounded-xl transition-colors ${
                                isCurrentActive ? 'bg-primary/10 text-primary animate-pulse' : 'bg-slate-50 text-slate-500'
                              }`}>
                                {getPolicyIcon(policy.id)}
                              </div>
                              <div>
                                <h2 className="text-xl sm:text-2xl font-bold text-slate-800 leading-tight">
                                  {highlightText(policy.title, searchQuery)}
                                </h2>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs font-semibold text-slate-400 bg-slate-50 px-2 py-0.5 rounded">
                                    Official Policy
                                  </span>
                                  <span className="text-xs text-slate-400 flex items-center gap-1 font-medium">
                                    <Clock className="h-3.5 w-3.5" /> {getReadingTime(policy.contentHtml)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            <button
                              onClick={() => handleCopyLink(policy.id)}
                              className="self-start sm:self-center flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-500 hover:text-primary hover:bg-primary/5 border border-slate-100 hover:border-primary/20 rounded-lg transition-all focus:outline-none"
                              title="Copy link to this section"
                            >
                              <Share2 className="h-3.5 w-3.5" />
                              <span>Share Link</span>
                            </button>
                          </div>

                          {/* Custom Policy HTML Content */}
                          <div
                            className="policy-content text-slate-600 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: policy.contentHtml }}
                          />
                        </CardContent>
                      </Card>
                    </section>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Floating Back to Top Button */}
        {showBackToTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 z-50 p-3 bg-primary text-primary-foreground rounded-full shadow-lg hover:bg-primary/95 hover:scale-110 active:scale-95 transition-all duration-200 focus:outline-none"
            aria-label="Back to top"
          >
            <ArrowUp className="h-5 w-5" />
          </button>
        )}
      </div>
    );
  };

  if (isPublicPath) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        {/* Public Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={logo} alt="Teamly Logo" className="h-9 w-auto object-contain" />
              <span className="h-5 w-px bg-slate-200 hidden sm:block" />
              <span className="font-semibold text-slate-500 text-sm hidden sm:block">Employee Portal</span>
            </div>
            <span className="text-xs font-semibold text-slate-400 bg-slate-50 px-2.5 py-1 rounded-full border border-slate-100">
              Policies Reference
            </span>
          </div>
        </header>

        {/* Page Main Content Area */}
        <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          {renderPageContent()}
        </main>

        {/* Public Footer */}
        <footer className="bg-white border-t border-slate-100 py-6 mt-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-slate-400">
            © {new Date().getFullYear()} Spatium Offices. All rights reserved.
          </div>
        </footer>
      </div>
    );
  }

  // If inside the CompanyLayout, render directly (as-is)
  return renderPageContent();
};

export default CompanyPolicies;
